import { Line } from 'react-chartjs-2';
import Paper from '@trackman/web-shared-components/components/Paper/Paper';
import { ChartData, ChartOptions } from 'chart.js';
import { NoData } from 'pages/Admin/Analytics/components/NoData/NoData';
import { SourceBadge } from 'pages/Admin/Analytics/components/SourceBadge/SourceBadge';
import { formatMonth, indexByMonth, mergeMonthKeys, parseMonthKey } from 'pages/Admin/Analytics/utils/monthlySeries';
import { useGetMonthlyChurnRates, useGetMonthlySubscriptionActivity } from 'queryHooks/Admin/analytics';

type ChurnData = { expired: number; baseSubscribers: number };

const buildChartOptions = (churnMeta: (ChurnData | null)[]): ChartOptions<'line'> => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: { boxWidth: 10, font: { size: 11 } },
    },
    tooltip: {
      callbacks: {
        label: (context) => {
          const value = Number(context.raw ?? 0);
          return context.dataset.yAxisID === 'y1'
            ? `${context.dataset.label}: ${value.toFixed(2)}%`
            : `${context.dataset.label}: ${value.toLocaleString()}`;
        },
        afterLabel: (context) => {
          if (context.dataset.yAxisID !== 'y1') return '';
          const meta = churnMeta[context.dataIndex];
          if (!meta) return '';
          return `Expired: ${meta.expired} / Base: ${meta.baseSubscribers}`;
        },
      },
    },
  },
  scales: {
    x: {
      grid: { color: 'rgba(0,0,0,0.04)' },
      ticks: { font: { size: 11 } },
    },
    y: {
      position: 'left',
      grid: { color: 'rgba(0,0,0,0.06)' },
      ticks: { font: { size: 11 } },
    },
    y1: {
      position: 'right',
      grid: { display: false },
      ticks: {
        font: { size: 11 },
        callback: (value) => `${Number(value)}%`,
      },
    },
  },
});

export const ChurnRateChart = () => {
  const { data: churnData, isLoading: isLoadingChurn, isError: isChurnError } = useGetMonthlyChurnRates();
  const { data: activityData, isLoading: isLoadingActivity, isError: isActivityError } = useGetMonthlySubscriptionActivity();

  const isLoading = isLoadingChurn || isLoadingActivity;
  const isError = isChurnError || isActivityError;
  const hasData = Boolean(churnData?.length || activityData?.length);

  const keys = mergeMonthKeys(activityData, churnData);

  const activityByMonth = indexByMonth(activityData);
  const churnByMonth = indexByMonth(churnData);

  const churnMeta: (ChurnData | null)[] = keys.map((key) => {
    const churn = churnByMonth.get(key);
    return churn ? { expired: churn.expired, baseSubscribers: churn.baseSubscribers } : null;
  });

  const chartData: ChartData<'line'> = {
    labels: keys.map((key) => {
      const { year, month } = parseMonthKey(key);
      return formatMonth(year, month);
    }),
    datasets: [
      {
        label: 'Renewals',
        data: keys.map((key) => activityByMonth.get(key)?.renewals ?? null),
        borderColor: '#22A05A',
        borderWidth: 2,
        tension: 0.4,
        yAxisID: 'y',
      },
      {
        label: 'Churn %',
        data: keys.map((key) => churnByMonth.get(key)?.rate ?? null),
        borderColor: '#D93025',
        borderWidth: 2,
        tension: 0.4,
        yAxisID: 'y1',
        borderDash: [4, 3],
      },
    ],
  };

  return (
    <Paper className='analytics-card analytics-chart-card'>
      <div className='analytics-card__title-group'>
        <span className='card-title'>Renewals & churn rate</span>
        <SourceBadge source='RECURLY' />
      </div>
      <p className='analytics-card__subtitle'>Monthly renewals against subscriber churn rate.</p>

      {isLoading || isError || !hasData ? (
        <NoData loading={isLoading} error={isError} noData={!hasData} />
      ) : (
        <div className='analytics-chart-card__content'>
          <Line data={chartData} options={buildChartOptions(churnMeta)} />
        </div>
      )}
    </Paper>
  );
};

export default ChurnRateChart;
